/**
 * @fileoverview Badge Celebration Queue Hook
 * 
 * Watches the user-badges-earned query cache for newly unlocked badges,
 * compares against localStorage to find uncelebrated ones, and exposes
 * a sequential queue for the overlay to consume.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { BADGE_DEFINITIONS, type BadgeDefinition } from '@/hooks/useBadges';

const CELEBRATED_KEY = 'celebrated-badges';
const AUTO_DISMISS_MS = 3000;

export interface CelebrationBadge {
  badge: BadgeDefinition;
  earnedAt?: string;
}

function getCelebratedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(CELEBRATED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function saveCelebratedSet(set: Set<string>) {
  localStorage.setItem(CELEBRATED_KEY, JSON.stringify([...set]));
}

export function useBadgeCelebrationQueue() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState<CelebrationBadge[]>([]);
  const [currentBadge, setCurrentBadge] = useState<CelebrationBadge | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const celebratedRef = useRef(getCelebratedSet());
  // Snapshot of badge IDs already present when this hook first sees data.
  // These are never celebrated even if localStorage was cleared.
  const baselineRef = useRef<Set<string> | null>(null);

  // Watch the earned badges query data
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.query.queryKey[0] === 'user-badges-earned' &&
        event.query.queryKey[1] === user.id
      ) {
        const earnedBadges = event.query.state.data as Array<{ badge_id: string; earned_at: string }> | undefined;
        if (!earnedBadges?.length) return;

        // On the very first data we see, snapshot all existing badges as baseline
        // so we never celebrate them (they were earned before this session).
        if (baselineRef.current === null) {
          baselineRef.current = new Set(earnedBadges.map(b => b.badge_id));
          // Also persist them as celebrated so future sessions skip them too
          const celebrated = getCelebratedSet();
          for (const id of baselineRef.current) {
            celebrated.add(id);
          }
          saveCelebratedSet(celebrated);
          celebratedRef.current = celebrated;
          return;
        }

        // Re-read from localStorage to avoid stale ref after remounts/refetches
        celebratedRef.current = getCelebratedSet();
        const celebrated = celebratedRef.current;
        const baseline = baselineRef.current;
        const newBadges: CelebrationBadge[] = [];

        for (const eb of earnedBadges) {
          // Skip if in baseline (existed before this session) or already celebrated
          if (baseline.has(eb.badge_id) || celebrated.has(eb.badge_id)) continue;
          const def = BADGE_DEFINITIONS.find(b => b.id === eb.badge_id);
          if (def) {
            newBadges.push({ badge: def, earnedAt: eb.earned_at });
          }
        }

        if (newBadges.length > 0) {
          setQueue(prev => {
            const existingIds = new Set(prev.map(b => b.badge.id));
            const unique = newBadges.filter(b => !existingIds.has(b.badge.id));
            return [...prev, ...unique];
          });
        }
      }
    });

    return () => unsubscribe();
  }, [user?.id, queryClient]);

  // Advance queue: show next badge when currentBadge is null and queue has items
  useEffect(() => {
    if (!currentBadge && queue.length > 0) {
      const [next, ...rest] = queue;
      setCurrentBadge(next);
      setQueue(rest);
    }
  }, [currentBadge, queue]);

  // Auto-dismiss timer
  useEffect(() => {
    if (!currentBadge) return;

    timerRef.current = setTimeout(() => {
      dismiss();
    }, AUTO_DISMISS_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentBadge]);

  const dismiss = useCallback(() => {
    if (currentBadge) {
      celebratedRef.current.add(currentBadge.badge.id);
      saveCelebratedSet(celebratedRef.current);
    }
    setCurrentBadge(null);
  }, [currentBadge]);

  return { queue, currentBadge, dismiss };
}
